<template>
  <div>
    <h1 class="p">
      {{ $t("webinars") }}
    </h1>

    <ProgressBar v-if="isLoading" mode="indeterminate" class="mb-3" />

    <p v-else-if="!webinars.length" class="p text-center text-color-secondary">
      {{ $t("no_webinars") }}
    </p>

    <div v-else class="grid">
      <div
        v-for="webinar in webinars"
        :key="webinar.id"
        class="col-12 sm:col-6 lg:col-4 xl:col-3 p-2"
        :class="webinar.videoUrl ? 'cursor-pointer' : null"
        @click="setShowFile(webinar)"
      >
        <div class="p-4 border-1 surface-border border-round h-full">
          <div class="mb-3 align-items-center">
            <ShowUserImage
              v-if="webinar.imageUrl"
              :url="webinar.imageUrl"
              class="no_rounded mr-0 responsive-bg-16by9"
            />
          </div>
          <div class="mb-2 font-bold">
            {{ formatSchedule(webinar) }}
          </div>
          <div class="mb-4 font-semibold">{{ webinar.title }}</div>
          <div v-if="webinar.authors" class="mb-2">
            Autor(es): {{ webinar.authors }}
          </div>
          <div>{{ webinar.description }}</div>
          <div v-if="webinar.isPaid" class="mb-2">
            <span class="event-type-tag event-type-tag--conference">
              {{ $t("webinar_paid") }}
            </span>
          </div>
          <div
            v-if="webinar.isPaid && formatPrice(webinar)"
            class="mt-1 font-semibold"
          >
            {{ formatPrice(webinar) }}
            <span
              v-if="webinar.pricePhaseName"
              class="font-normal text-color-secondary"
            >
              · {{ webinar.pricePhaseName }}
            </span>
          </div>
          <div
            v-if="webinar.isPaid"
            class="webinar-card__actions mt-3"
            @click.stop
          >
            <Button
              v-if="webinar.isRegistered"
              :label="$t('conference_registered_label')"
              class="p-button-sm p-button-outlined w-full justify-content-center"
              disabled
            />
            <Button
              v-else-if="webinar.soldOut"
              :label="$t('conference_sold_out')"
              class="p-button-sm p-button-outlined p-button-secondary w-full justify-content-center"
              disabled
            />
            <Button
              v-else-if="webinar.canPurchase"
              :label="$t('conference_pay')"
              class="p-button-sm w-full justify-content-center"
              :disabled="!webinar.paymentLandingPageUrl"
              @click="openLanding(webinar)"
            />
            <p
              v-if="webinar.canPurchase && !webinar.paymentLandingPageUrl"
              class="mt-2 text-sm text-color-secondary"
            >
              {{ $t("conference_landing_missing") }}
            </p>
          </div>
          <div
            v-else-if="canRegister(webinar)"
            class="webinar-card__actions mt-3"
            @click.stop
          >
            <Button
              v-if="webinar.isRegistered"
              :label="$t('webinar_cancel_registration')"
              class="p-button-sm p-button-text webinar-card__cancel-btn"
              :loading="cancellingId === webinar.id"
              @click="cancelRegistration(webinar)"
            />
            <Button
              v-else
              :label="$t('webinar_register')"
              class="p-button-sm webinar-card__register-btn w-full"
              :loading="registeringId === webinar.id"
              @click="register(webinar)"
            />
          </div>
        </div>
      </div>
    </div>

    <DialogFiles
      :display-files="showFile"
      :files="[{ url: fileUrl, name: fileName }]"
      autoplay
      @closed="showFile = false"
    />
  </div>
</template>

<script>
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import Button from "primevue/button";
import ProgressBar from "primevue/progressbar";
import DialogFiles from "../files/DialogFiles.vue";
import ShowUserImage from "../files/ShowUserImage.vue";

dayjs.extend(utc);

export default {
  components: { Button, ProgressBar, DialogFiles, ShowUserImage },
  data() {
    return {
      webinars: [],
      isLoading: false,
      showFile: false,
      fileUrl: null,
      fileName: null,
      registeringId: null,
      cancellingId: null,
    };
  },
  created() {
    this.getWebinars();
  },
  methods: {
    getWebinars() {
      this.isLoading = true;
      this.$root
        .apiGet(this.$root.api.profileWebinars)
        .then((response) => {
          this.isLoading = false;
          this.webinars = response.data.data || [];
        })
        .catch((error) => {
          this.isLoading = false;
          this.webinars = [];
          const msgCode =
            error.response &&
            error.response.data &&
            error.response.data.msg_code;
          if (msgCode) {
            this.$root.toastError(msgCode);
          }
        });
    },
    extractTime(value) {
      if (!value) {
        return null;
      }
      const raw = typeof value === "object" && value.date ? value.date : value;
      const match = String(raw).match(/[T ](\d{2}:\d{2})/);
      return match ? match[1] : null;
    },
    formatSchedule(webinar) {
      if (!webinar || !webinar.startDate) {
        return "";
      }
      const startDate = this.$root.formatDate(webinar.startDate);
      const endDate =
        webinar.endDate && this.$root.formatDate(webinar.endDate) !== startDate
          ? this.$root.formatDate(webinar.endDate)
          : null;
      const startTime = this.extractTime(webinar.startTime);
      const endTime = this.extractTime(webinar.endTime);

      let result = endDate ? `${startDate} - ${endDate}` : startDate;
      if (startTime) {
        result += ` · ${startTime}`;
        if (endTime && endTime !== startTime) {
          result += ` - ${endTime}`;
        }
      }
      return result;
    },
    canRegister(webinar) {
      if (typeof webinar.canRegister === "boolean") {
        return webinar.canRegister;
      }
      if (!webinar || !webinar.startDate) {
        return false;
      }
      const now = dayjs();
      const start = dayjs.utc(webinar.startDate).local();
      if (webinar.videoUrl && start.isBefore(now)) {
        return false;
      }
      if (webinar.endDate) {
        return dayjs.utc(webinar.endDate).local().isAfter(now);
      }
      return start.isAfter(now) || start.isSame(now, "minute");
    },
    register(webinar) {
      this.registeringId = webinar.id;
      this.$root
        .apiPost(`${this.$root.api.profileWebinars}/${webinar.id}/register`)
        .then((response) => {
          this.registeringId = null;
          webinar.isRegistered = true;
          this.$root.toastSuccess(this.$t(response.data.msg_code));
        })
        .catch((error) => {
          this.registeringId = null;
          const msgCode =
            error.response &&
            error.response.data &&
            error.response.data.msg_code;
          if (msgCode) {
            this.$root.toastError(msgCode);
          }
        });
    },
    cancelRegistration(webinar) {
      this.cancellingId = webinar.id;
      this.$root
        .apiDelete(`${this.$root.api.profileWebinars}/${webinar.id}/register`)
        .then((response) => {
          this.cancellingId = null;
          webinar.isRegistered = false;
          const msgCode =
            response.data && response.data.msg_code
              ? response.data.msg_code
              : "webinar_registration_cancelled";
          this.$root.toastSuccess(this.$t(msgCode));
        })
        .catch((error) => {
          this.cancellingId = null;
          const msgCode =
            error.response &&
            error.response.data &&
            error.response.data.msg_code;
          if (msgCode) {
            this.$root.toastError(msgCode);
          }
        });
    },
    formatPrice(webinar) {
      if (!webinar || webinar.price == null || webinar.price === "") {
        return "";
      }
      const amount = Number(webinar.price);
      if (Number.isNaN(amount)) {
        return "";
      }
      const formatted = amount.toLocaleString("pt-PT", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      const currency =
        webinar.currency && webinar.currency !== "eur"
          ? webinar.currency
          : "€";
      return `${formatted} ${currency}`;
    },
    openLanding(webinar) {
      if (webinar && webinar.paymentLandingPageUrl) {
        window.open(webinar.paymentLandingPageUrl, "_blank", "noopener");
        return;
      }
      this.$root.toastError(this.$t("conference_landing_missing"));
    },
    setShowFile(webinar) {
      if (!webinar.videoUrl) {
        return;
      }
      if (
        webinar.videoUrl.startsWith("files/") ||
        webinar.videoUrl.startsWith("https://quadrante.")
      ) {
        this.fileUrl = webinar.videoUrl;
        this.fileName = webinar.title;
        this.showFile = true;
      } else {
        window.open(webinar.videoUrl, "_blank");
      }
    },
  },
};
</script>

<style scoped>
.event-type-tag {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.event-type-tag--conference {
  background: #fff1f2;
  color: #be123c;
  border: 1px solid #fecdd3;
}

.webinar-card__actions {
  --agenda-primary: #0c233f;
  --agenda-accent: #0f97c7;
  --agenda-accent-dark: #0b7ea8;
  --agenda-accent-soft: rgba(15, 151, 199, 0.1);
  --agenda-muted: #64748b;
  --agenda-surface: #f8fafc;
}

.webinar-card__register-btn.p-button {
  white-space: nowrap;
  font-size: 0.75rem !important;
  padding: 0.35rem 0.75rem !important;
  border-radius: 6px !important;
  background: var(--agenda-accent) !important;
  border-color: var(--agenda-accent) !important;
  color: #fff !important;
}

.webinar-card__register-btn.p-button:hover {
  background: var(--agenda-accent-dark) !important;
  border-color: var(--agenda-accent-dark) !important;
}

.webinar-card__cancel-btn.p-button,
.webinar-card__cancel-btn.p-button:enabled:hover,
.webinar-card__cancel-btn.p-button:enabled:active,
.webinar-card__cancel-btn.p-button:enabled:focus {
  white-space: nowrap;
  font-size: 0.75rem !important;
  padding: 0.2rem 0.45rem !important;
  color: var(--agenda-muted) !important;
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
}

.webinar-card__cancel-btn.p-button:enabled:hover {
  color: var(--agenda-primary) !important;
  background: var(--agenda-surface) !important;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.webinar-card__cancel-btn.p-button:enabled:active {
  color: var(--agenda-accent-dark) !important;
  background: var(--agenda-accent-soft) !important;
  text-decoration: none;
}
</style>
